/*
 * Device info collector for audited POS finance actions.
 *
 * Gathers a stable, best-effort snapshot of the terminal — fingerprint (device
 * trust id), device name/host, model, OS, platform, app version and LAN ip —
 * to attach to day opening/closing and cash-out requests for security & audit.
 * The backend adds the server-observed public ip and parsed user-agent (see
 * deviceContext.util.js); the client never asserts its own public ip.
 *
 * Everything is defensively guarded so it degrades gracefully across native,
 * web and Tauri builds, and the result is cached for the session.
 */

import { Platform } from 'react-native';
import * as Device from 'expo-device';
import { getOrCreateDeviceTrustId } from './storage';

let cachedInfo = null;

/** Best-effort LAN ip via expo-network (native), swallowing any failure. */
async function readLocalIp() {
    try {
        // eslint-disable-next-line global-require
        const Network = require('expo-network');

        if (typeof Network.getIpAddressAsync === 'function') {
            return await Network.getIpAddressAsync();
        }
    } catch (_e) {
        // expo-network unavailable (e.g. web) — ignore.
    }

    return '';
}

/** Extras from react-native-device-info (native), each call individually guarded. */
function readDeviceInfoExtras() {
    const extras = {};

    try {
        // eslint-disable-next-line global-require
        const DeviceInfo = require('react-native-device-info').default;

        try {
            extras.appVersion = DeviceInfo.getVersion?.() || '';
        } catch (_e) { /* noop */ }
        try {
            extras.host = DeviceInfo.getHostSync?.() || '';
        } catch (_e) { /* noop */ }
        try {
            extras.deviceId = DeviceInfo.getDeviceId?.() || '';
        } catch (_e) { /* noop */ }
    } catch (_e) {
        // react-native-device-info unavailable — ignore.
    }

    return extras;
}

/** Web/Tauri host + user agent, when a DOM is present. */
function readWebContext() {
    const web = {};

    try {
        if (typeof navigator !== 'undefined' && navigator.userAgent) {
            web.userAgent = navigator.userAgent;
        }

        if (typeof window !== 'undefined' && window.location && window.location.hostname) {
            web.host = window.location.hostname;
        }
    } catch (_e) {
        // No DOM — ignore.
    }

    return web;
}

/**
 * Collect the device snapshot for the current terminal (cached per session).
 *
 * @returns {Promise<object>} `{ fingerprint, deviceId, name, host, model, brand, os, platform, appVersion, localIp, userAgent }`
 */
export async function getDeviceInfo() {
    if (cachedInfo) {
        return cachedInfo;
    }

    const info = {
        platform: Platform.OS || '',
        fingerprint: '',
        deviceId: '',
        name: '',
        host: '',
        model: '',
        brand: '',
        os: '',
        appVersion: '',
        localIp: '',
        userAgent: '',
    };

    try {
        info.fingerprint = (await getOrCreateDeviceTrustId()) || '';
    } catch (_e) { /* noop */ }

    try {
        info.deviceId = Device.osBuildId || Device.osInternalBuildId || '';
        info.name = Device.deviceName || '';
        info.model = Device.modelName || '';
        info.brand = Device.brand || Device.manufacturer || '';
        info.os = [Device.osName, Device.osVersion].filter(Boolean).join(' ');
    } catch (_e) { /* noop */ }

    const extras = readDeviceInfoExtras();

    info.appVersion = extras.appVersion || info.appVersion;
    info.host = extras.host || info.host;
    info.deviceId = info.deviceId || extras.deviceId || '';

    info.localIp = await readLocalIp();

    const web = readWebContext();

    info.userAgent = web.userAgent || info.userAgent;
    info.host = info.host || web.host || info.name || '';

    cachedInfo = info;

    return info;
}

/**
 * Header form of the device identity, mirroring what the login/2FA flow already
 * sends so the backend's existing `x-device-*` readers stay consistent.
 *
 * @returns {Promise<{'x-device-id': string, 'x-device-name': string}>}
 */
export async function getDeviceHeaders() {
    const info = await getDeviceInfo();
    const name = [info.name || info.host, info.os].filter(Boolean).join(' / ');

    return {
        'x-device-id': info.fingerprint || info.deviceId || '',
        'x-device-name': name,
    };
}

export default {
    getDeviceInfo,
    getDeviceHeaders,
};
